import { FC, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  deleteCalendarConnection,
  fetchCalendarIntegrations,
  saveCalendarConnectionPreference,
  type CalendarIntegrationCapabilityRecord,
  type CalendarIntegrationCapabilityState,
  type CalendarIntegrationProviderRecord,
  type CalendarIntegrationProviderState,
  type CalendarIntegrationResponse,
} from './calendar-connections-api';

type SaveState = Record<string, boolean>;

const providerAccentClass = (provider: string) => {
  if (provider === 'google') {
    return 'bg-danger-transparent text-danger';
  }

  if (provider === 'microsoft') {
    return 'bg-info-transparent text-info';
  }

  return 'bg-light text-default';
};

const stateBadgeClass = (state: CalendarIntegrationCapabilityState | CalendarIntegrationProviderState) => {
  switch (state) {
    case 'connected':
      return 'bg-success-transparent text-success';
    case 'degraded':
      return 'bg-warning-transparent text-warning';
    case 'enabled_user_action_required':
      return 'bg-primary-transparent text-primary';
    case 'enabled_provider_unavailable':
      return 'bg-secondary-transparent text-secondary';
    case 'revoked':
      return 'bg-danger-transparent text-danger';
    case 'disabled_by_policy':
    default:
      return 'bg-light text-default';
  }
};

const stateLabel = (state: CalendarIntegrationCapabilityState | CalendarIntegrationProviderState) => {
  switch (state) {
    case 'connected':
      return 'Available now';
    case 'degraded':
      return 'Needs reconnection';
    case 'enabled_user_action_required':
      return 'Action required';
    case 'enabled_provider_unavailable':
      return 'Provider unavailable';
    case 'revoked':
      return 'Revoked by policy';
    case 'disabled_by_policy':
    default:
      return 'Disabled by policy';
  }
};

const scopeLabel = (scopeType: string) => {
  switch (scopeType) {
    case 'group':
      return 'Department policy';
    case 'service_line':
      return 'Service line policy';
    case 'schedule_type':
      return 'Schedule type policy';
    case 'company':
    default:
      return 'Company policy';
  }
};

const syncPreferenceLabel = (provider: CalendarIntegrationProviderRecord) =>
  provider.sync_preference_options.find((option) => option.value === provider.sync_preference)?.label ?? 'No sync preference yet';

const capabilityDescription = (label: string, capability: CalendarIntegrationCapabilityRecord) => {
  if (capability.state === 'disabled_by_policy') {
    return `${label} is not available for your current schedule context. CARES keeps any saved preference inactive until policy allows it.`;
  }

  if (capability.requires_published_schedule) {
    return `${label} can be used when a schedule is published for your current context.`;
  }

  return `${label} is available under the current policy.`;
};

const providerStateDescription = (provider: CalendarIntegrationProviderRecord) => {
  switch (provider.connection_state) {
    case 'disabled_by_policy':
      return `${provider.label} stays visible here, but the current policy does not allow this provider for your schedule context.`;
    case 'enabled_provider_unavailable':
      return `${provider.label} is allowed by policy, but OAuth is not configured in this environment yet.`;
    case 'enabled_user_action_required':
      return provider.sync_preference === 'none'
        ? `Choose how you want CARES to use ${provider.label}, then connect when you are ready.`
        : `Your preference is saved. Connect ${provider.label} to finish setup.`;
    case 'degraded':
      return `${provider.label} was connected before, but the current token needs to be refreshed before CARES can rely on it.`;
    case 'revoked':
      return `${provider.label} has an existing connection record, but the current policy no longer allows CARES to use it.`;
    case 'connected':
    default:
      return `${provider.label} is connected and inside the current scheduling policy boundary.`;
  }
};

const deliveryModeLabel = (mode: string) => {
  switch (mode) {
    case 'new_events_only':
      return 'New events only';
    case 'overwrite_existing':
      return 'May overwrite matching events';
    case 'none':
    default:
      return 'No delivery';
  }
};

const dataProfileLabels: Record<string, string> = {
  title: 'Schedule title',
  location: 'Location',
  role_or_service_label: 'Role or service label',
  request_reason: 'Request reason',
  admin_annotations: 'Admin annotations',
  private_comments: 'Private comments',
  contact_details: 'Contact details',
  internal_notes: 'Internal notes',
};

const CalendarConnectionsPanel: FC = () => {
  const [response, setResponse] = useState<CalendarIntegrationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<SaveState>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provider = params.get('calendar_provider');
    const result = params.get('calendar_result');

    if (provider && result) {
      const providerLabel = provider === 'google' ? 'Google Calendar' : provider === 'microsoft' ? 'Microsoft 365' : provider;

      if (result === 'connected') {
        setSuccess(`${providerLabel} connected.`);
      } else if (result === 'not_ready') {
        setError(`${providerLabel} OAuth is not configured in this environment yet.`);
      } else {
        setError(`Calendar connection failed for ${providerLabel}. Try again.`);
      }

      params.delete('calendar_provider');
      params.delete('calendar_result');
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', nextUrl);
    }

    let active = true;

    const loadIntegrations = async () => {
      try {
        const nextResponse = await fetchCalendarIntegrations();
        if (!active) {
          return;
        }

        setResponse(nextResponse);
        setError(null);
      } catch (requestError) {
        if (!active) {
          return;
        }

        setError(requestError instanceof Error ? requestError.message : 'Unable to load calendar integration settings.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void loadIntegrations();

    return () => {
      active = false;
    };
  }, []);

  const updateDraft = (providerKey: string, updates: Partial<CalendarIntegrationProviderRecord>) => {
    setResponse((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        providers: current.providers.map((provider) => (
          provider.provider === providerKey ? { ...provider, ...updates } : provider
        )),
      };
    });
  };

  const reloadIntegrations = async () => {
    const nextResponse = await fetchCalendarIntegrations();
    setResponse(nextResponse);
  };

  const saveConnection = async (provider: CalendarIntegrationProviderRecord) => {
    setSaving((current) => ({ ...current, [provider.provider]: true }));
    setError(null);
    setSuccess(null);

    try {
      await saveCalendarConnectionPreference({
        provider: provider.provider,
        sync_preference: provider.sync_preference,
        notify_when_ready: provider.notify_when_ready,
      });
      await reloadIntegrations();
      setSuccess(`${provider.label} preferences saved.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to save calendar integration settings.');
    } finally {
      setSaving((current) => ({ ...current, [provider.provider]: false }));
    }
  };

  const disconnectConnection = async (provider: CalendarIntegrationProviderRecord) => {
    setSaving((current) => ({ ...current, [provider.provider]: true }));
    setError(null);
    setSuccess(null);

    try {
      await deleteCalendarConnection(provider.provider);
      await reloadIntegrations();
      setSuccess(`${provider.label} disconnected.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to disconnect this calendar provider.');
    } finally {
      setSaving((current) => ({ ...current, [provider.provider]: false }));
    }
  };

  if (loading) {
    return (
      <Card className="border custom-card mb-0">
        <Card.Body className="d-flex align-items-center gap-2 text-muted">
          <Spinner size="sm" animation="border" />
          Loading calendar integrations...
        </Card.Body>
      </Card>
    );
  }

  if (!response) {
    return <Alert variant="danger">{error ?? 'Calendar integrations could not be loaded.'}</Alert>;
  }

  const includedFields = Object.entries(response.data_profile.includes)
    .filter(([, included]) => included)
    .map(([key]) => dataProfileLabels[key] ?? key);
  const withheldFields = Object.entries(response.data_profile.includes)
    .filter(([, included]) => !included)
    .map(([key]) => dataProfileLabels[key] ?? key);

  return (
    <>
      <Alert variant="info">
        This integrations tab reads the current calendar policy for {response.selected_group.name}. Provider access, export tools, and data-sharing limits are resolved server-side before any action is shown here.
      </Alert>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Row className="g-3 mb-3">
        <Col xl={7}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body className="d-flex flex-column gap-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Badge bg="" className="bg-primary-transparent text-primary">
                  {scopeLabel(response.resolved_scope.scope_type)}
                </Badge>
                <Badge bg="" className="bg-light text-default">
                  {response.data_profile.profile_key} data profile
                </Badge>
                {response.resolved_scope.policy_version ? (
                  <Badge bg="" className="bg-light text-default">
                    Policy v{response.resolved_scope.policy_version}
                  </Badge>
                ) : null}
              </div>
              <div>
                <h5 className="mb-2">What CARES may share outside the platform</h5>
                <p className="text-muted mb-0">
                  Events sent through allowed exports or provider delivery use the <span className="fw-medium text-default">{response.data_profile.redaction_label}</span> profile for this schedule context.
                </p>
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <div className="rounded border p-3 h-100">
                    <div className="text-success text-uppercase fs-12 fw-semibold mb-2">Included</div>
                    <div className="d-flex flex-wrap gap-2">
                      {includedFields.map((field) => (
                        <Badge key={field} bg="" className="bg-success-transparent text-success">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="rounded border p-3 h-100">
                    <div className="text-muted text-uppercase fs-12 fw-semibold mb-2">Not shared</div>
                    <div className="d-flex flex-wrap gap-2">
                      {withheldFields.map((field) => (
                        <Badge key={field} bg="" className="bg-light text-default border">
                          {field}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={5}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body className="d-flex flex-column gap-3">
              <div>
                <p className="text-primary text-uppercase fs-12 fw-semibold mb-2">Resolved policy boundary</p>
                <h5 className="mb-2">Current schedule context</h5>
                <p className="text-muted mb-0">
                  Company: <span className="fw-medium text-default">{response.selected_company.name}</span>
                  <br />
                  Department: <span className="fw-medium text-default">{response.selected_group.name}</span>
                  <br />
                  Service line: <span className="fw-medium text-default">{response.selected_service_line?.name ?? 'Not selected'}</span>
                  <br />
                  Schedule type: <span className="fw-medium text-default">{response.selected_schedule_type?.name ?? 'Not selected'}</span>
                </p>
              </div>
              <div className="rounded border p-3">
                <div className="text-muted text-uppercase fs-12 fw-semibold mb-2">Matched policies</div>
                {response.policy.matched_policies.length > 0 ? (
                  <div className="d-flex flex-column gap-2">
                    {response.policy.matched_policies.map((policy) => (
                      <div key={`${policy.scope_type}-${policy.policy_id}`} className="d-flex align-items-center justify-content-between gap-3">
                        <span className="fw-medium text-default">{scopeLabel(policy.scope_type)}</span>
                        <Badge bg="" className="bg-light text-default border">
                          v{policy.policy_version}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">No published policy matched, so CARES is holding this surface in deny-all mode.</p>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <h5 className="fw-medium mb-1">Export or download</h5>
                  <p className="text-muted fs-13 mb-0">Manual exports that let you take a schedule file out of CARES.</p>
                </div>
                <Badge bg="" className={stateBadgeClass(response.capabilities.schedule_export_download.state)}>
                  {stateLabel(response.capabilities.schedule_export_download.state)}
                </Badge>
              </div>
              <p className="text-muted fs-13 mb-0">
                {capabilityDescription('Export or download', response.capabilities.schedule_export_download)}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="border custom-card mb-0 h-100">
            <Card.Body className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div>
                  <h5 className="fw-medium mb-1">Subscribe feed</h5>
                  <p className="text-muted fs-13 mb-0">Read-only subscription feeds for approved personal calendar tools.</p>
                </div>
                <Badge bg="" className={stateBadgeClass(response.capabilities.schedule_subscribe_feed.state)}>
                  {stateLabel(response.capabilities.schedule_subscribe_feed.state)}
                </Badge>
              </div>
              <p className="text-muted fs-13 mb-0">
                {capabilityDescription('Subscribe feed', response.capabilities.schedule_subscribe_feed)}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        {response.providers.map((provider) => {
          const preferencesLocked = provider.connection_state === 'disabled_by_policy' || provider.connection_state === 'revoked';
          const canConnect = Boolean(provider.connect_url)
            && (provider.connection_state === 'enabled_user_action_required' || provider.connection_state === 'degraded');
          const canDisconnect = Boolean(provider.disconnect_url)
            && ['connected', 'degraded', 'revoked'].includes(provider.connection_state);

          return (
            <Col xl={6} key={provider.provider}>
              <Card className="border custom-card mb-0 h-100">
                <Card.Body className="d-flex flex-column gap-3">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <h5 className="fw-medium mb-0">{provider.label}</h5>
                        <Badge bg="" className={providerAccentClass(provider.provider)}>
                          {provider.provider === 'google' ? 'Google' : 'Microsoft'}
                        </Badge>
                        <Badge bg="" className={stateBadgeClass(provider.connection_state)}>
                          {stateLabel(provider.connection_state)}
                        </Badge>
                      </div>
                      <p className="text-muted fs-13 mb-0">{providerStateDescription(provider)}</p>
                      {provider.connected_email ? (
                        <p className="text-muted fs-12 mb-0 mt-2">
                          Connected account: <span className="fw-medium text-default">{provider.connected_email}</span>
                        </p>
                      ) : null}
                    </div>
                    {provider.auth_identity_linked ? (
                      <Badge bg="" className="bg-success-transparent text-success">
                        Sign-in linked
                      </Badge>
                    ) : null}
                  </div>

                  <div className="rounded border p-3">
                    <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
                      <span className="fw-medium text-default">Busy-time import</span>
                      <Badge bg="" className={stateBadgeClass(provider.busy_time_import_state)}>
                        {stateLabel(provider.busy_time_import_state)}
                      </Badge>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <span className="fw-medium text-default">Schedule delivery</span>
                      <div className="d-flex align-items-center gap-2">
                        <Badge bg="" className={stateBadgeClass(provider.schedule_delivery_state)}>
                          {stateLabel(provider.schedule_delivery_state)}
                        </Badge>
                        <Badge bg="" className="bg-light text-default border">
                          {deliveryModeLabel(provider.schedule_delivery_mode)}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <Form.Group>
                    <Form.Label>Preferred sync behavior</Form.Label>
                    <Form.Select
                      value={provider.sync_preference}
                      disabled={preferencesLocked || Boolean(saving[provider.provider])}
                      onChange={(event) => updateDraft(provider.provider, { sync_preference: event.target.value })}
                    >
                      {provider.sync_preference_options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Current preference: {syncPreferenceLabel(provider)}
                    </Form.Text>
                  </Form.Group>

                  <Form.Check
                    id={`notify-when-ready-${provider.provider}`}
                    type="switch"
                    disabled={preferencesLocked || Boolean(saving[provider.provider])}
                    label="Notify me when this provider is ready or needs attention"
                    checked={provider.notify_when_ready}
                    onChange={(event) => updateDraft(provider.provider, { notify_when_ready: event.target.checked })}
                  />

                  {provider.provider_notes ? (
                    <Alert variant="light" className="mb-0">
                      {provider.provider_notes}
                    </Alert>
                  ) : null}

                  <div className="d-flex align-items-center justify-content-between gap-3 mt-auto">
                    <p className="text-muted fs-12 mb-0">
                      {preferencesLocked
                        ? 'Saved settings stay visible but inactive while this provider is outside the current policy boundary.'
                        : provider.oauth_ready
                          ? 'Connection consent is separate from sign-in. Save your preference whenever you change it.'
                          : 'OAuth is not configured here yet, so connection setup is intentionally unavailable.'}
                    </p>
                    <div className="d-flex align-items-center gap-2">
                      {canDisconnect ? (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={Boolean(saving[provider.provider])}
                          onClick={() => void disconnectConnection(provider)}
                        >
                          {saving[provider.provider] ? 'Working...' : 'Disconnect'}
                        </Button>
                      ) : null}
                      {canConnect && provider.connect_url ? (
                        <Button as="a" href={provider.connect_url} variant="outline-primary" size="sm">
                          {provider.connection_state === 'degraded' ? 'Reconnect' : 'Connect'}
                        </Button>
                      ) : null}
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={preferencesLocked || Boolean(saving[provider.provider])}
                        onClick={() => void saveConnection(provider)}
                      >
                        {saving[provider.provider] ? 'Saving...' : 'Save preferences'}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

export default CalendarConnectionsPanel;
